import {
  AnalyticsData,
  CrashesAnalyticsPayload,
  FeedbacksAnalyticsPayload,
  MetadataList,
  NetworkHealth,
  NetworkSpeed,
  ReleaseHealthOverview,
  ReleaseVersionList,
  ScoringConfig,
  SdkInitializationsAnalytics,
  SdkInitializationsAnalyticsPayload,
  SearchValues,
  TestingCoverage,
  UsersAnalyticsPayload,
} from "@common/models/Analytics.model";
import ApiService from "@common/services/Api.service";
import { AxiosResponse } from "axios";

export class AnalyticsService {
  private apiService: ApiService;

  constructor() {
    this.apiService = ApiService.getInstance();
  }

  getCrashAnalytics(
    teamId: string,
    appId: string,
    payload: CrashesAnalyticsPayload,
    abortSignal?: AbortSignal,
  ): Promise<AxiosResponse<AnalyticsData>> {
    return this.apiService.get({
      resource: `analytics/${teamId}/apps/${appId}/crashes?start_date=${payload.start_date}&end_date=${
        payload.end_date
      }&group=${payload.group}${payload.app_version ? `&app_version=${payload.app_version}` : ""}`,
      config: {
        signal: abortSignal,
      },
    });
  }

  getUserFeedbackAnalytics(
    teamId: string,
    appId: string,
    payload: FeedbacksAnalyticsPayload,
    abortSignal?: AbortSignal,
  ): Promise<AxiosResponse<AnalyticsData>> {
    return this.apiService.get({
      resource: `analytics/${teamId}/apps/${appId}/issues?start_date=${payload.start_date}&end_date=${
        payload.end_date
      }&group=${payload.group}${payload.app_version ? `&app_version=${payload.app_version}` : ""}${
        payload.tag ? `&tag=${payload.tag}` : ""
      }`,
      config: {
        signal: abortSignal,
      },
    });
  }

  getUsersAnalytics(
    teamId: string,
    appId: string,
    payload: UsersAnalyticsPayload,
    abortSignal?: AbortSignal,
  ): Promise<AxiosResponse<AnalyticsData>> {
    return this.apiService.get({
      resource: `analytics/${teamId}/apps/${appId}/users?start_date=${payload.start_date}&end_date=${payload.end_date}&group=${payload.group}`,
      config: {
        signal: abortSignal,
      },
    });
  }

  getSdkInitializationsAnalytics(
    teamId: string,
    payload: SdkInitializationsAnalyticsPayload,
    abortSignal?: AbortSignal,
  ): Promise<AxiosResponse<SdkInitializationsAnalytics>> {
    return this.apiService.get({
      resource: `analytics/${teamId}/devices?start_date=${payload.start_date}&end_date=${payload.end_date}&group=${
        payload.group || "week"
      }`,
      config: {
        signal: abortSignal,
      },
    });
  }

  getReleases(
    teamId: string,
    appId: string,
    params: { search?: string; offset?: number; limit?: number } = {},
    abortSignal?: AbortSignal,
  ): Promise<AxiosResponse<ReleaseVersionList>> {
    const { search, offset = 0, limit = 5 } = params;
    const query = new URLSearchParams({ offset: String(offset), limit: String(limit) });
    if (search) query.append("search", search);

    return this.apiService.get({
      resource: `analytics/${teamId}/apps/${appId}/health/releases?${query}`,
      config: {
        signal: abortSignal,
      },
    });
  }

  getReleaseStats(
    teamId: string,
    appId: string,
    appVersion: string,
    abortSignal?: AbortSignal,
  ): Promise<AxiosResponse<ReleaseHealthOverview>> {
    return this.apiService.get({
      resource: `analytics/${teamId}/apps/${appId}/health/release_health?app_version=${encodeURIComponent(appVersion)}`,
      config: {
        signal: abortSignal,
      },
    });
  }

  getNetworkHealthDetails(
    teamId: string,
    appId: string,
    appVersion: string,
    offset = 0,
    limit = 100,
    abortSignal?: AbortSignal,
  ): Promise<AxiosResponse<NetworkHealth>> {
    return this.apiService.get({
      resource: `analytics/${teamId}/apps/${appId}/health/network_health?app_version=${encodeURIComponent(
        appVersion,
      )}&offset=${offset.toString()}&limit=${limit.toString()}`,
      config: {
        signal: abortSignal,
      },
    });
  }

  getNetworkSpeedDetails(
    teamId: string,
    appId: string,
    appVersion: string,
    offset = 0,
    limit = 100,
    abortSignal?: AbortSignal,
  ): Promise<AxiosResponse<NetworkSpeed>> {
    return this.apiService.get({
      resource: `analytics/${teamId}/apps/${appId}/health/network_speed?app_version=${encodeURIComponent(
        appVersion,
      )}&offset=${offset.toString()}&limit=${limit.toString()}`,
      config: {
        signal: abortSignal,
      },
    });
  }

  getTestingCoverageDetails(
    teamId: string,
    appId: string,
    appVersion: string,
    abortSignal?: AbortSignal,
  ): Promise<AxiosResponse<TestingCoverage>> {
    return this.apiService.get({
      resource: `analytics/${teamId}/apps/${appId}/health/testing_coverage?app_version=${encodeURIComponent(
        appVersion,
      )}`,
      config: {
        signal: abortSignal,
      },
    });
  }

  getScoringConfig(
    teamId: string,
    appId: string,
    metric: string,
    version: string,
    abortSignal?: AbortSignal,
  ): Promise<AxiosResponse<ScoringConfig>> {
    return this.apiService.get({
      resource: `analytics/${teamId}/apps/${appId}/scoring/${metric}${
        version ? `?app_version=${encodeURIComponent(version)}` : ""
      }`,
      config: {
        signal: abortSignal,
      },
    });
  }

  putScoringConfig(
    teamId: string,
    appId: string,
    metric: string,
    payload: Record<string, unknown>,
    abortSignal?: AbortSignal,
  ): Promise<AxiosResponse<ScoringConfig>> {
    return this.apiService.put({
      resource: `analytics/${teamId}/apps/${appId}/scoring/${metric}`,
      data: payload,
      config: {
        signal: abortSignal,
      },
    });
  }

  generateReleaseSummary(
    teamId: string,
    appId: string,
    version: string,
    abortSignal?: AbortSignal,
  ): Promise<AxiosResponse<ScoringConfig>> {
    return this.apiService.post({
      resource: `analytics/${teamId}/apps/${appId}/health/release_summary/regenerate${
        version ? `?app_version=${encodeURIComponent(version)}` : ""
      }`,
      config: {
        signal: abortSignal,
      },
    });
  }

  getMetadataKeys(teamId: string, appId: string, abortSignal?: AbortSignal): Promise<AxiosResponse<MetadataList>> {
    return this.apiService.get({
      resource: `analytics/${teamId}/apps/${appId}/scoring/testing_coverage/metadata_keys`,
      config: {
        signal: abortSignal,
      },
    });
  }

  getSearchValues(
    teamId: string,
    appId: string,
    key: string,
    abortSignal?: AbortSignal,
  ): Promise<AxiosResponse<SearchValues>> {
    return this.apiService.get({
      resource: `analytics/${teamId}/apps/${appId}/scoring/testing_coverage/values/${key}`,
      config: {
        signal: abortSignal,
      },
    });
  }
}
